import { ComponentConfig } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import {
  imageProps,
  ImageProps,
} from '../../../page-builder/puck/reusable-props/image-props';
import {
  paddingFields,
  PaddingProps,
} from '../../../page-builder/puck/reusable-props/padding';
import { imageField } from '../../../page-builder/puck/reusable-props/image';
import { IAsset } from '../../../page-builder/interface/apiServiceInterface';
import { AxiosInstance } from 'axios';
import CustomTooltip from '../../../components/common/tooltip';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { ReduxSelectorProps } from '../../services/constants';
import { hyperLinkFields, HyperLinkProps } from './hyperLink';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import { IBackgroundProps } from '../customComponents/BackgroungColorComponent';
import LazyLoad from 'react-lazyload';
import { useState } from 'react';

export type ImageUploaderProps = {
  imageFile: IAsset;
  border: IBorderValueProps;
  background: IBackgroundProps;
  overflow: string;
  lazyLoader: boolean;
  fetchPriority: boolean;
} & MarginProps &
  PaddingProps &
  showComponentProps &
  ImageProps &
  HyperLinkProps;

export interface ImageUploaderComponentProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const ImageUploader = ({
  useAppSelector,
  baseService,
}: ImageUploaderComponentProps): ComponentConfig<ImageUploaderProps> => {
  return {
    label: (
      <CustomTooltip title="ImageUploader">
        <span>Image Uploader</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      imageFile: { ...imageField(true, baseService).file },
      ...imageProps([], true),
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
      overflow: {
        type: 'radio',
        options: [
          { label: 'Hidden', value: 'overflow-hidden' },
          { label: 'Scroll', value: 'overflow-scroll' },
        ],
      },
      lazyLoader: {
        type: 'radio',
        label: 'Lazy Loader',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      fetchPriority: {
        type: 'radio',
        label: 'Fetch Priority',
        options: [
          { label: 'On', value: true },
          { label: 'Off', value: false },
        ],
      },
      ...backgroungFields,
      ...hyperLinkFields,
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      background: {
        color: '#ffffff',
        isInherit: true,
      },
      overflow: 'overflow-hidden',
      lazyLoader: false,
      fetchPriority: false,
      imageFile: {
        code: '',
        imageUrl: '',
        fileName: '',
        storeID: '',
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },

      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
      image: {
        height: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        maxWidth: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        justifyContent: {
          desktop: 'justify-start',
          mobile: 'justify-start',
          tablet: 'justify-start',
        },
        deviceType: 'desktop',
      },
      hyperlink: {
        useLink: false,
        link: '',
      },
    },
    render: ({
      margin,
      padding,
      border,
      imageFile,
      image,
      showComponent,
      hyperlink,
      editMode,
      overflow,
      background,
      lazyLoader,
      fetchPriority,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const url = process.env.REACT_APP_S3_BUCKET_URL + imageFile.imageUrl;
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const imgHeight = isNaN(+image?.height[currentDeviceType])
        ? image?.height[currentDeviceType]
        : +image?.height[currentDeviceType];

      const imgWidth = isNaN(+image?.maxWidth[currentDeviceType])
        ? image?.maxWidth[currentDeviceType]
        : +image?.maxWidth[currentDeviceType];

      const htmlWidth = imageFile?.width;

      const htmlHeight = imageFile?.height;
      const [isLoaded, setIsLoaded] = useState(false);
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`flex ${image?.justifyContent?.[currentDeviceType]}`}>
              <div
                style={{
                  ...getPaddingStyles(padding, currentDeviceType),
                  ...marginStyles,
                  ...getBorderProps(border, currentDeviceType),
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                }}
                className={`${border?.borderStyle?.[currentDeviceType]} ${hyperlink?.useLink && hyperlink?.link.length ? 'cursor-pointer' : ''} ${overflow}`}
                onClick={() => {
                  if (hyperlink?.useLink && hyperlink?.link.length) {
                    window.open(hyperlink?.link, '_blank');
                  }
                }}>
                {lazyLoader && !editMode ? (
                  <LazyLoad
                    height={imgHeight}
                    offset={100}
                    once
                    placeholder={
                      <div
                        style={{
                          width: imgWidth,
                          height: imgHeight,
                          backgroundColor: '#f0f0f0',
                          borderRadius: '4px',
                        }}
                      />
                    }>
                    <img
                      src={url}
                      alt={imageFile?.fileName}
                      decoding="async"
                      loading="lazy"
                      width={htmlWidth}
                      height={htmlHeight}
                      style={
                        isLoaded
                          ? {
                              width: imgWidth,
                              height: imgHeight,
                              maxWidth: 'unset',
                            }
                          : { maxHeight: imgHeight, maxWidth: imgWidth } // no style before load
                      }
                      onLoad={() => setIsLoaded(true)}
                    />
                  </LazyLoad>
                ) : (
                  <img
                    src={url}
                    alt={imageFile?.fileName}
                    decoding="async"
                    fetchPriority={fetchPriority ? 'high' : undefined}
                    width={htmlWidth}
                    height={htmlHeight}
                    style={
                      isLoaded || editMode
                        ? {
                            width: imgWidth,
                            height: imgHeight,
                            maxWidth: 'unset',
                          }
                        : { maxHeight: imgHeight, maxWidth: imgWidth }
                    }
                    onLoad={() => setIsLoaded(true)}
                  />
                )}
              </div>
            </div>
          )}
        </>
      );
    },
  };
};
